'use client';

import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  AIDAPTIVE_COHORT_KEY,
  END_USER_REGISTERED_KEY,
} from '@/constants/cookie';
import { registerEndUser } from '@/services/platform-api/register-end-user';
import { logAidaptiveCohort } from '@/utils/aidaptive-analytics';

declare global {
  interface Window {
    gaGlobal?: { vid?: string };
  }
}

type EndUserRegistrationContextType = {
  /** True once the GA client ID has been resolved (or retries exhausted). */
  ready: boolean;
  /** The resolved GA client ID, or null if still pending / unavailable. */
  cid: string | null;
};

const EndUserRegistrationContext =
  createContext<EndUserRegistrationContextType>({
    ready: false,
    cid: null,
  });

const getCookie = (name: string): string | undefined => {
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name + '=([^;]*)')
  );
  return match ? decodeURIComponent(match[1]) : undefined;
};

const setSessionCookie = (name: string, value: string) => {
  document.cookie = `${name}=${encodeURIComponent(value)};path=/`;
};

const clearSessionCookie = (name: string) => {
  document.cookie = `${name}=;path=/;max-age=0`;
};

const TAG = '[EndUserRegistration]';

const fetchGoogleAnalyticsId = (
  callback: (id: string) => void,
  retryIndex = 0
) => {
  if (window.gaGlobal?.vid) {
    console.debug(TAG, 'Got GA ID from gaGlobal.vid:', window.gaGlobal.vid);
    callback(window.gaGlobal.vid);
    return;
  }

  const retryDelay = [0, 200, 500, 1000, 2000][retryIndex];
  if (retryDelay !== undefined) {
    console.debug(
      TAG,
      `GA not ready (gaGlobal: ${!!window.gaGlobal}). ` +
        `Retry ${retryIndex + 1}/5 in ${retryDelay}ms`
    );
    setTimeout(
      () => fetchGoogleAnalyticsId(callback, retryIndex + 1),
      retryDelay
    );
  } else {
    console.debug(TAG, 'GA ID fetch exhausted all retries, giving up');
    callback('');
  }
};

export const EndUserRegistrationProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [ready, setReady] = useState(() => {
    if (typeof document === 'undefined') return false;
    return getCookie(END_USER_REGISTERED_KEY) !== undefined;
  });
  const [cid, setCid] = useState<string | null>(() => {
    if (typeof document === 'undefined') return null;
    return getCookie(END_USER_REGISTERED_KEY) || null;
  });

  useEffect(() => {
    const existing = getCookie(END_USER_REGISTERED_KEY);

    if (existing !== undefined && existing !== '') {
      setCid(existing);
      setReady(true);
    }

    console.debug(TAG, 'Resolving GA client id for end-user registration…');
    fetchGoogleAnalyticsId((resolvedCid) => {
      if (!resolvedCid) {
        console.debug(TAG, 'GA ID is empty, aborting registration');
        setReady(true);
        if (existing !== undefined) {
          const cohortCookie = getCookie(AIDAPTIVE_COHORT_KEY);
          if (cohortCookie !== undefined) {
            logAidaptiveCohort(cohortCookie);
          }
        }
        return;
      }

      setCid(resolvedCid);
      setReady(true);

      const cookieMatchesGa =
        existing !== undefined && existing !== '' && existing === resolvedCid;

      if (cookieMatchesGa) {
        console.debug(
          TAG,
          `CID matches "${END_USER_REGISTERED_KEY}" cookie, skipping registerEndUser`
        );
        const cohortCookie = getCookie(AIDAPTIVE_COHORT_KEY);
        if (cohortCookie !== undefined) {
          logAidaptiveCohort(cohortCookie);
        }
        return;
      }

      if (
        existing !== undefined &&
        existing !== '' &&
        existing !== resolvedCid
      ) {
        console.debug(
          TAG,
          `"${END_USER_REGISTERED_KEY}" cookie does not match GA client id (cookie="${existing}", ga="${resolvedCid}"), re-registering`
        );
      } else {
        console.debug(
          TAG,
          'No matching registration cookie, calling registerEndUser…'
        );
      }

      clearSessionCookie(AIDAPTIVE_COHORT_KEY);

      console.debug(
        TAG,
        `Calling registerEndUser(cid="${resolvedCid}", url="${window.location.href}")`
      );
      registerEndUser(resolvedCid, window.location.href)
        .then(({ success, cohort }) => {
          console.debug(
            TAG,
            `registerEndUser returned success=${success}, cohort=${cohort ?? '(none)'}`
          );
          setSessionCookie(
            END_USER_REGISTERED_KEY,
            success ? resolvedCid : ''
          );
          if (cohort && cohort !== 'unknown') {
            setSessionCookie(AIDAPTIVE_COHORT_KEY, cohort);
            logAidaptiveCohort(cohort);
          }
        })
        .catch((error: unknown) => {
          // network failures reaching the server action are expected; don't let them through to Sentry
          console.debug(
            TAG,
            'registerEndUser request failed:',
            (error as Error)?.message ?? error
          );
        });
    });
  }, []);

  const value = useMemo(() => ({ ready, cid }), [ready, cid]);

  return (
    <EndUserRegistrationContext.Provider value={value}>
      {children}
    </EndUserRegistrationContext.Provider>
  );
};

export const useEndUserRegistration = (): EndUserRegistrationContextType => {
  return useContext(EndUserRegistrationContext);
};
